import { api } from './api.ts';
import { fromBase64, toBase64 } from './crypto/primitives.ts';
import { wrapBytesFor, wrapPrivateKey, unwrapPrivateKey } from './crypto/keypair.ts';
import { deriveInviteKey, generateInviteSecret } from './crypto/kdf.ts';
import { requireOrganisation, requireVault, sorgeFuerOrganisationsSchluessel } from './crypto/vault.ts';

/**
 * Schließt für eine bereits bestehende Akten-Safe-Person die fehlende
 * kryptografische Lücke in einer WEITEREN Organisation (Migration 007/012).
 * Legt KEINE Mitgliedschaft an — die muss über die Verwaltung bereits aktiv
 * sein (`org_mitgliedschaften`, Migration 010); ohne sie weist der Server das
 * Annehmen ab. Ausführliche Begründung in `backend/src/routes/einladungen.ts`
 * — hier nur die Client-Seite.
 *
 * Das Geheimnis ist überall dieselbe Base64-Zeichenkette: einmal erzeugt
 * (`aussprechen`), landet sie im Fragment-Anker des Links (verlässt den
 * Browser nie über einen HTTP-Request) und wird beim Annehmen unverändert
 * wieder eingelesen — nur so hasht der Server auf beiden Seiten dasselbe.
 */

export interface OffeneEinladung {
  id: string;
  email: string;
  rolle: string;
  erstellt_am: string;
  laeuft_ab: string;
}

export interface AusgesprocheneEinladung {
  id: string;
  mailVersendet: boolean;
  /** Nur gesetzt, wenn der Mailversand fehlschlug — Rückfall zum Weitergeben von Hand. */
  link: string | null;
}

/**
 * Spricht eine Einladung aus: erzeugt ein Einmal-Geheimnis, leitet daraus
 * einen Schlüssel ab und wrappt den Organisationsschlüssel damit. Der Server
 * bekommt das Geheimnis nur transient, um die Mail zu verschicken — er
 * speichert ausschließlich dessen Hash.
 */
export async function einladungAussprechen(email: string): Promise<AusgesprocheneEinladung> {
  const organisation = requireOrganisation();
  const secret = generateInviteSecret();
  const secretBase64 = toBase64(secret);
  const einladungsSchluessel = await deriveInviteKey(secret);
  const wrappedOrgPrivateKey = toBase64(
    await wrapPrivateKey(organisation.privateKeyPkcs8, einladungsSchluessel),
  );

  return api.post<AusgesprocheneEinladung>('/api/einladungen', {
    email,
    secret: secretBase64,
    wrappedOrgPrivateKey,
  });
}

export async function offeneEinladungen(): Promise<OffeneEinladung[]> {
  const { einladungen } = await api.get<{ einladungen: OffeneEinladung[] }>('/api/einladungen');
  return einladungen;
}

/** Liest Einladungs-Id und Geheimnis aus der aktuellen Adresse, falls vorhanden. */
export function einladungAusAdresse(): { id: string; secretBase64: string } | null {
  const parameter = new URLSearchParams(window.location.search);
  const id = parameter.get('einladung');
  const secretBase64 = window.location.hash.replace(/^#/, '');
  if (!id || !secretBase64) return null;
  return { id, secretBase64 };
}

/**
 * Nimmt eine Einladung an: entpackt den Organisationsschlüssel mit dem aus
 * dem Geheimnis abgeleiteten Schlüssel, wrappt ihn sofort für die eigene
 * Person neu und schließt die aktuell gewählte Organisation gleich mit auf —
 * ohne zweiten Termin mit der einladenden Person (Kommentar in Migration 007).
 *
 * Setzt eine bestehende Anmeldung voraus (Konto muss bereits existieren).
 */
export async function einladungAnnehmen(id: string, secretBase64: string): Promise<string> {
  const vault = requireVault();
  const secret = fromBase64(secretBase64);
  const einladungsSchluessel = await deriveInviteKey(secret);

  const geprueft = await api.post<{ kundeId: string; rolle: string; wrappedOrgPrivateKey: string }>(
    `/api/einladungen/${id}/pruefen`,
    { secret: secretBase64 },
  );

  const orgPrivateKeyPkcs8 = await unwrapPrivateKey(
    fromBase64(geprueft.wrappedOrgPrivateKey),
    einladungsSchluessel,
  );
  const wrappedPrivateKeyForSelf = toBase64(
    await wrapBytesFor(orgPrivateKeyPkcs8, vault.publicKeyRaw, vault.algorithm),
  );

  const ergebnis = await api.post<{ ok: boolean; kundeId: string }>(`/api/einladungen/${id}/annehmen`, {
    secret: secretBase64,
    wrappedPrivateKeyForSelf,
  });

  // Gleich entsperren, statt erst beim nächsten Login — der Umschalter in
  // der Kopfzeile zeigt die neue Organisation dann sofort an.
  await sorgeFuerOrganisationsSchluessel(ergebnis.kundeId);
  return ergebnis.kundeId;
}
